import { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronDown, ChevronUp, Download, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useClickOutside } from '../../hooks/useClickOutside';
import useEscapeKey from '../../hooks/useEscapeKey';
import { useNotifications } from '../../contexts/NotificationContext';
import { useProjectContext } from '../../contexts/ProjectContext';
import { useAuth } from '../../hooks/useAuth';
import { reportService } from '../../services';
import { projectService } from '../../services/project.service';
import { ReportDetails } from '../../types';
import { SaveProjectDialog } from './SaveProjectDialog';
import CropDiagramDialog from './CropDiagramDialog';
import { captureDiagram } from '../../utils/diagramCapture';
import { useReactFlow } from 'reactflow';
import { previewProjectReport } from '../../utils/pdfReportGenerator';

// Import components directly instead of through the barrel file
import { ReportCustomizationDialogProps } from './report-customization/types';
import { ReportDetailsForm } from './report-customization/ReportDetailsForm';
import { DiagramSection } from './report-customization/DiagramSection';
import { SearchableGroupList } from './report-customization/SearchableGroupList';
import { DIAGRAM_IMAGE_KEY, NODE_TYPE_GROUPS } from './report-customization/constants';
import { compressImage, getDefaultSections } from './report-customization/utils';

function ReportCustomizationDialog({ onClose, initialDiagramImage }: ReportCustomizationDialogProps) {
  // Hooks
  const { showNotification } = useNotifications();
  const { project: currentProject, saveProject, renameProject, shareProject } = useProjectContext();
  const { user, isAdmin } = useAuth();

  // Check if project is saved
  const isUnsavedProject = currentProject?.id === 'unsaved-project';

  // State for report details
  const [title, setTitle] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [authorRole, setAuthorRole] = useState('');
  const [authorEmail, setAuthorEmail] = useState('');
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [salesContact, setSalesContact] = useState('');
  const [diagramImage, setDiagramImage] = useState<string | undefined>(initialDiagramImage);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [autoShare, setAutoShare] = useState(false);

  // State for UI
  // const [isVisible, setIsVisible] = useState(true); // Controlled by parent
  const [isLoading, setIsLoading] = useState(false);
  const [salesContacts, setSalesContacts] = useState<any[]>([]);
  const [selectedSalesContactInfo, setSelectedSalesContactInfo] = useState<any | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Create a ref that changes when the component is mounted and tracks if data has been loaded
  const dataLoadedRef = useRef(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get React Flow instance for diagram capture
  const reactFlowInstance = useReactFlow();

  // Function to capture diagram - now with a force parameter
  const captureDiagramIfNeeded = useCallback(async (force = false, autoCrop = true) => {
    // Check if we already have a diagram image in localStorage
    const storedImage = localStorage.getItem(DIAGRAM_IMAGE_KEY);
    
    if (storedImage && !force) {
      setDiagramImage(storedImage);
      return storedImage;
    } else if (reactFlowInstance) {
      // If no stored image or force is true, capture the diagram
      try {
        setIsLoading(true);
        // Fit the view before capturing
        reactFlowInstance.fitView(); 
        // Capture the diagram with white background and auto-cropping for PDF
        const capturedImage = await captureDiagram(undefined, {
          addWhiteBackground: true, // Add white background for PDF
          cropEmptySpace: autoCrop // Use the autoCrop parameter to control cropping
        });
        
        // Set the image
        setDiagramImage(capturedImage);
        localStorage.setItem(DIAGRAM_IMAGE_KEY, capturedImage);
        
        if (force) {
          showNotification('success', 'Diagram refreshed successfully');
        }
        
        return capturedImage;
      } catch (captureError) {
        console.error('Error capturing diagram:', captureError);
        showNotification('error', 'Failed to capture diagram');
        return undefined;
      } finally {
        setIsLoading(false);
      }
    }
    return undefined;
  }, [reactFlowInstance, showNotification]);

  // Function to load report details - now with a force parameter
  const loadReportDetails = useCallback(async (force = false) => {
    // Skip if data is already loaded and not forcing a refresh
    if (dataLoadedRef.current && !force) {
      console.log('Report data already loaded and not forcing refresh, skipping loadReportDetails');
      return;
    }
    
    console.log('Loading report details, force =', force);
    setIsLoading(true);
    try {
      // Load report settings
      let dbReportDetails: ReportDetails | null = null;
      let localReportDetails = reportService.getReportFromLocalStorage();
      
      console.log('Local report details from storage:', localReportDetails);
      
      // If project is saved, get report details from database
      if (currentProject && !isUnsavedProject) {
        try {
          dbReportDetails = await reportService.getProjectReportDetails(currentProject.id);
          console.log('Database report details:', dbReportDetails);
        } catch (dbError) {
          console.error('Error loading report details from database:', dbError);
          // Continue with local settings if database fetch fails
        }
      }
      
      // Determine which settings to use (local or database)
      // For unsaved projects, always use local settings if available
      // For saved projects, prioritize database settings
      const reportDetails = isUnsavedProject ? localReportDetails : (dbReportDetails || localReportDetails);
      console.log('Using report details:', reportDetails);
      
      if (reportDetails) {
        setTitle(reportDetails.title || currentProject?.name || 'Custom Report');
        setAuthorName(reportDetails.authorName || '');
        setAuthorRole(reportDetails.ownerRole || '');
        setAuthorEmail(reportDetails.authorEmail || user?.email || '');
        setSelectedSections(reportDetails.selectedSections || getDefaultSections(currentProject));
        setSalesContact(reportDetails.salesContact || '');
        
        // If report has a diagram image from the database, use it
        if (reportDetails.diagramImage) {
          setDiagramImage(reportDetails.diagramImage);
        } 
        // Otherwise check localStorage or use initialDiagramImage
        else if (!diagramImage) {
          const storedImage = localStorage.getItem(DIAGRAM_IMAGE_KEY);
          if (storedImage) {
            setDiagramImage(storedImage);
          } else if (initialDiagramImage) {
            setDiagramImage(initialDiagramImage);
          }
        }
        
        // Set autoShare based on saved preference, default to false (unchecked)
        if (reportDetails.autoShare !== undefined) {
          // Use saved preference if available
          setAutoShare(reportDetails.autoShare);
        } else {
          // Default to false (unchecked) for all projects
          setAutoShare(false);
        }
      } else {
        // Default values if no settings found
        console.log('No report details found, using defaults');
        setTitle(currentProject?.name || 'Custom Report');
        setAuthorName('');
        setAuthorRole('');
        setAuthorEmail(user?.email || '');
        setSelectedSections(getDefaultSections(currentProject));
        setSalesContact('');
        setAutoShare(false);
        
        // Check localStorage or use initialDiagramImage for diagram
        if (!diagramImage) {
          const storedImage = localStorage.getItem(DIAGRAM_IMAGE_KEY);
          if (storedImage) {
            setDiagramImage(storedImage);
          } else if (initialDiagramImage) {
            setDiagramImage(initialDiagramImage);
          }
        }
      }
      
      // Mark data as loaded
      dataLoadedRef.current = true;
      console.log('Report data loaded successfully');
      
      if (force) {
        showNotification('success', 'Report data refreshed successfully');
      }
    } catch (error) {
      console.error('Error loading report details:', error);
      showNotification('error', 'Failed to load report details');
      
      // Set default values
      setTitle(currentProject?.name || 'Custom Report');
      setAuthorName('');
      setAuthorRole('');
      setAuthorEmail(user?.email || '');
      setSelectedSections(getDefaultSections(currentProject));
      setSalesContact('');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [currentProject, isUnsavedProject, user, showNotification, initialDiagramImage, diagramImage]);

  // Auto-capture and auto-crop diagram when component mounts if no image exists
  useEffect(() => {
    const autoCaptureDiagram = async () => {
      // Check if we already have a diagram image in localStorage
      const storedImage = localStorage.getItem(DIAGRAM_IMAGE_KEY);
      
      if (!storedImage && reactFlowInstance) {
        console.log('No diagram image found in localStorage, auto-capturing and auto-cropping');
        try {
          await captureDiagramIfNeeded(true, true); // Force capture and auto-crop
        } catch (error) {
          console.error('Error auto-capturing diagram:', error);
        }
      }
    };
    
    autoCaptureDiagram();
  }, [reactFlowInstance, captureDiagramIfNeeded]);

  // Load report details when the dialog is opened
  useEffect(() => {
    // Skip if data is already loaded
    if (dataLoadedRef.current) {
      console.log('Report data already loaded, skipping load');
      return;
    }
    
    // Force load from localStorage for unsaved projects
    if (isUnsavedProject) {
      console.log('Loading report details from localStorage for unsaved project');
      const localReportDetails = reportService.getReportFromLocalStorage();
      console.log('Local report details:', localReportDetails);
      
      if (localReportDetails) {
        setTitle(localReportDetails.title || currentProject?.name || 'Custom Report');
        setAuthorName(localReportDetails.authorName || '');
        setAuthorRole(localReportDetails.ownerRole || '');
        setAuthorEmail(localReportDetails.authorEmail || user?.email || '');
        setSelectedSections(localReportDetails.selectedSections || getDefaultSections(currentProject));
        setSalesContact(localReportDetails.salesContact || '');
        setAutoShare(localReportDetails.autoShare || false);
        
        // Check for diagram image in localStorage
        const storedImage = localStorage.getItem(DIAGRAM_IMAGE_KEY);
        if (storedImage) {
          setDiagramImage(storedImage);
        } else if (initialDiagramImage) {
          setDiagramImage(initialDiagramImage);
        }
        
        // Mark data as loaded to prevent overwriting
        dataLoadedRef.current = true;
        console.log('Report details loaded from localStorage successfully');
      } else {
        console.log('No local report details found, loading defaults');
        // If no local settings, load defaults
        loadReportDetails();
      }
    } else {
      console.log('Loading report details for saved project');
      // For saved projects, use the normal loading function
      loadReportDetails();
    }
  }, [isUnsavedProject, currentProject, user, initialDiagramImage, loadReportDetails]); // Added loadReportDetails to dependency array

  // Load sales contacts
  useEffect(() => {
    const loadSalesContacts = async () => {
      try {
        const contacts = await reportService.getSalesContacts();
        setSalesContacts(contacts);
        // If user is a sales rep, auto-select them
        if (user?.email?.endsWith('@metarouter.io')) {
          const userContact = contacts.find((c) => c.email === user.email);
          if (userContact) {
            setSalesContact(userContact.email);
          }
        }
      } catch (error) {
        console.error('Error loading sales contacts:', error);
        showNotification('error', 'Failed to load sales contacts');
      }
    };
    loadSalesContacts();
  }, [user, showNotification]);

  // Update selected sales contact info when salesContact changes
  useEffect(() => {
    if (salesContact) {
      const contact = salesContacts.find(c => c.email === salesContact);
      setSelectedSalesContactInfo(contact || null);
    } else {
      setSelectedSalesContactInfo(null);
    }
  }, [salesContact, salesContacts]);

  // Populate DESTINATIONS group with destinations from the canvas
  useEffect(() => {
    if (reactFlowInstance && currentProject) {
      // Get all nodes from the React Flow instance
      const nodes = reactFlowInstance.getNodes();
      
      // Filter for destination nodes
      const destinationNodes = nodes.filter(node => node.type === 'destination');
      
      // Clear the existing destinations
      NODE_TYPE_GROUPS.DESTINATIONS = [];
      
      // Add each destination to the NODE_TYPE_GROUPS.DESTINATIONS array
      destinationNodes.forEach(node => {
        const nodeData = node.data || {};
        const name = nodeData.name || nodeData.label || 'Unnamed Destination';
        
        // Add to the destinations group
        NODE_TYPE_GROUPS.DESTINATIONS.push({
          id: node.id,
          name: name
        });
      });
    }
  }, [reactFlowInstance, currentProject]);

  // Save report settings
  const handleSave = async () => {
    if (!currentProject) {
      throw new Error('No project loaded');
    }
    
    setIsLoading(true);
    try {
      // Compress diagram image if it exists
      let compressedDiagramImage = diagramImage;
      if (diagramImage) {
        try {
          compressedDiagramImage = await compressImage(diagramImage);
        } catch (compressionError) {
          console.error('Error compressing diagram image:', compressionError);
          // Continue with original image if compression fails
        }
      } else {
        // If no diagram image, try to capture one
        const capturedImage = await captureDiagramIfNeeded();
        if (capturedImage) {
          compressedDiagramImage = capturedImage;
        }
      }

      const reportData: ReportDetails = {
        title: title.trim(),
        authorName: authorName.trim(),
        ownerRole: authorRole.trim(),
        authorEmail: authorEmail.trim(),
        selectedSections,
        salesContact: salesContact.trim(),
        salesContactInfo: selectedSalesContactInfo || undefined,
        autoShare // Save the autoShare preference
        // diagramImage removed - should only be stored locally
      };
      
      // Always save diagram image to localStorage
      if (compressedDiagramImage) {
        localStorage.setItem(DIAGRAM_IMAGE_KEY, compressedDiagramImage);
      }

      if (isUnsavedProject) {
        await Promise.resolve(reportService.saveReportToLocalStorage(reportData));
      } else {
        await reportService.updateProjectReportDetails(currentProject.id, reportData);
      }

      // Return the saved data
      return reportData;
    } catch (error) {
      console.error('Error saving report settings:', error);
      if (error instanceof Error && error.message.includes('413')) {
        showNotification('error', 'Image is too large. Please try cropping it to a smaller size.');
      } else {
        showNotification('error', 'Failed to save report settings');
      }
      throw error; // Re-throw to be caught by handleClose
    } finally {
      setIsLoading(false);
    }
  };

  const performClose = async () => {
    try {
      await handleSave(); // Save settings before closing
    } catch (error) {
      console.error('Error saving report settings during close:', error);
      // Potentially notify user, but still proceed to close
    } finally {
      onClose(); // Call parent's onClose
    }
  };

  // Hook for clicking outside dialog
  const dialogRef = useClickOutside<HTMLDivElement>(() => {
    if (!showSaveDialog && !showCropDialog) { // Only close if nested dialogs are not open
      performClose();
    }
  });
  useEscapeKey(performClose); // Use escape key to close the dialog

  // Toggle specific section
  const handleSectionToggle = (section: string) => {
    setSelectedSections((prev) => {
      // Ensure prev is an array
      const prevSections = Array.isArray(prev) ? prev : [];
      return prevSections.includes(section) 
        ? prevSections.filter((s) => s !== section) 
        : [...prevSections, section];
    });
  };

  // Toggle entire group
  const handleGroupToggle = (
    groupName: string,
    items: { id: string; name: string }[],
    isTopicGroup?: boolean
  ) => {
    setSelectedSections((prev) => {
      // Ensure prev is an array
      const prevSections = Array.isArray(prev) ? prev : [];
      
      const groupPrefix = isTopicGroup ? '' : `${groupName}:`;
      const itemIds = items.map((item) => (isTopicGroup ? item.name : `${groupPrefix}${item.id}`));

      const allSelected = itemIds.every((id) => prevSections.includes(id));
      if (allSelected) {
        return prevSections.filter((s) => !itemIds.includes(s) && s !== groupName);
      } else {
        const newSections = [...prevSections];
        if (!newSections.includes(groupName)) {
          newSections.push(groupName);
        }
        itemIds.forEach((id) => {
          if (!newSections.includes(id)) {
            newSections.push(id);
          }
        });
        return newSections;
      }
    });
  };

  // Show crop dialog
  const handleCropDiagram = () => {
    setShowCropDialog(true);
  };

  // Save the cropped diagram to local storage
  const handleCropComplete = async (croppedImageBase64: string) => {
    try {
      // Compress the cropped image
      const compressedImage = await compressImage(croppedImageBase64);
      setDiagramImage(compressedImage);
      setShowCropDialog(false);

      // Save to local storage
      localStorage.setItem(DIAGRAM_IMAGE_KEY, compressedImage);

      // Save report data without the diagram image
      const reportData: ReportDetails = {
        title,
        authorName,
        ownerRole: authorRole,
        authorEmail,
        selectedSections,
        salesContact,
        salesContactInfo: selectedSalesContactInfo || undefined,
        autoShare // Save the autoShare preference
      };

      if (isUnsavedProject) {
        await Promise.resolve(reportService.saveReportToLocalStorage(reportData));
      } else if (currentProject) { // Ensure currentProject is not null
        await reportService.updateProjectReportDetails(currentProject.id, reportData);
      }
    } catch (error) {
      console.error('Error saving new diagram:', error);
      if (error instanceof Error && error.message.includes('413')) {
        showNotification('error', 'Image is too large. Please try cropping to a smaller size.');
      } else {
        showNotification('error', 'Failed to save diagram image');
      }
    }
  };

  // Share project and get link
  const handleShareProject = async () => {
    if (!currentProject) return null;
    
    try {
      // Get the preview ID from current project or generate a new one
      let previewId = currentProject.settings?.preview;
      
      if (!previewId) {
        const result = await shareProject(currentProject.id, '');
        
        if (result && typeof result === 'object' && !('message' in result)) {
          if (!result.settings?.preview) {
            throw new Error('Failed to generate preview ID');
          }
          previewId = result.settings.preview;
          
          // Set public access to Can View
          await projectService.setProjectPublicAccess(currentProject.id, 'Can View');
        }
      }
      
      return `${window.location.origin}/projects/${currentProject.id}?preview=${previewId}`;
    } catch (error) {
      console.error('Error sharing project:', error);
      showNotification('error', 'Failed to share project');
      return null;
    }
  };

  // Handle refreshing the report data and diagram
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Force capture a new diagram
      await captureDiagramIfNeeded(true);
      
      // Force reload report details
      await loadReportDetails(true);
    } catch (error) {
      console.error('Error refreshing report data:', error);
      showNotification('error', 'Failed to refresh report data');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Generate and preview the PDF report
  const handlePreviewReport = async () => {
    if (!currentProject) {
      showNotification('error', 'No project loaded');
      return;
    }
    
    // Validate required fields
    if (!title.trim()) {
      showNotification('error', 'Report title is required');
      return;
    }
    
    if (!authorEmail.trim()) {
      showNotification('error', 'Email is required');
      return;
    }
    
    // Validate email format
    const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!EMAIL_REGEX.test(authorEmail)) {
      showNotification('error', 'Please enter a valid email address');
      return;
    }
    
    // Check if diagram has been captured
    if (!diagramImage) {
      showNotification('warning', 'Capturing diagram before generating report');
      const capturedImage = await captureDiagramIfNeeded(true); // Force capture
      if (!capturedImage) {
        showNotification('error', 'Failed to capture diagram');
        return;
      }
      // Update diagramImage state with the newly captured image
      setDiagramImage(capturedImage); 
    }
    
    setIsLoading(true);
    try {
      // Compress the diagram image for the preview
      // Use the potentially updated diagramImage from the check above
      let finalDiagramImage = diagramImage; 
      if (finalDiagramImage) {
        try {
          finalDiagramImage = await compressImage(finalDiagramImage);
        } catch (compressionError) {
          console.error('Error compressing diagram image for preview:', compressionError);
          // Continue with original image if compression fails
        }
      } else {
        showNotification('error', 'No diagram image available');
        setIsLoading(false); // Ensure loading state is reset
        return;
      }

      // Save settings first (this will also save the compressed image)
      await handleSave();

      // If autoShare is enabled, get the share link
      let shareLink: string | null = null;
      if (autoShare && !isUnsavedProject) {
        shareLink = await handleShareProject();
      }
      
      const reportDetails: ReportDetails = {
        title,
        authorName,
        ownerRole: authorRole,
        authorEmail,
        selectedSections,
        salesContact,
        salesContactInfo: selectedSalesContactInfo || undefined,
        diagramImage: finalDiagramImage, // Use the final (potentially compressed) image
        // Only include shareLink if autoShare is enabled and not null
        ...(shareLink ? { shareLink } : {})
      };
      
      // This is where the PDF is actually generated and opened in a new tab
      await previewProjectReport(currentProject, reportDetails, finalDiagramImage); // Pass final image
      showNotification('success', 'Report preview opened in a new tab');
    } catch (error) {
      console.error('Error previewing report:', error);
      if (error instanceof Error && error.message.includes('413')) {
        showNotification('error', 'Image is too large. Please try cropping it to a smaller size.');
      } else {
        showNotification('error', 'Failed to preview report');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Save the project before customizing the report
  const handleSaveProject = async (name: string) => {
    if (!currentProject) return;
    try {
      setIsLoading(true);
      renameProject(name); // This updates the project name in context
      await saveProject(); // This saves the project to the backend

      const projectResult = currentProject; // currentProject should now have the new ID if it was 'unsaved-project'
      if (projectResult && projectResult.id !== 'unsaved-project') {
        showNotification('success', 'Project saved successfully');

        // Compress diagram image if it exists
        let compressedDiagramImage = diagramImage;
        if (diagramImage) {
          try {
            compressedDiagramImage = await compressImage(diagramImage);
          } catch (compressionError) {
            console.error('Error compressing diagram image:', compressionError);
            // Continue with original image if compression fails
          }
        } else {
          // If no diagram image, try to capture one
          const capturedImage = await captureDiagramIfNeeded();
          if (capturedImage) {
            compressedDiagramImage = capturedImage;
          }
        }

        const reportData: ReportDetails = {
          title: name, // Use the new project name as the report title
          authorName,
          ownerRole: authorRole,
          authorEmail,
          selectedSections,
          salesContact,
          salesContactInfo: selectedSalesContactInfo || undefined,
          autoShare // Save the autoShare preference
        };
        
        // Save diagram image to localStorage only
        if (compressedDiagramImage) {
          localStorage.setItem(DIAGRAM_IMAGE_KEY, compressedDiagramImage);
        }

        await reportService.updateProjectReportDetails(projectResult.id, reportData);
        reportService.clearReportFromLocalStorage(); // Clear local storage since it's now saved to DB
        
        // Ensure the dialog closes and the main report dialog content is shown
        setShowSaveDialog(false); 
        // Re-load report details for the now-saved project
        await loadReportDetails(true); 

      }
    } catch (error) {
      console.error('Error saving project:', error);
      if (error instanceof Error && error.message.includes('413')) {
        showNotification('error', 'Image is too large. Please try cropping it to a smaller size.');
      } else {
        showNotification('error', 'Failed to save project');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormChange = (field: string, value: string) => {
    switch (field) {
      case 'title':
        setTitle(value);
        break;
      case 'authorName':
        setAuthorName(value);
        break;
      case 'authorRole':
        setAuthorRole(value);
        break;
      case 'authorEmail':
        setAuthorEmail(value);
        break;
      case 'salesContact':
        setSalesContact(value);
        break;
      default:
        break;
    }
  };

  // Main dialog structure
  const mainDialogContent = (
    <motion.div
      ref={dialogRef}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-[900px] mx-4 border border-gray-200 dark:border-gray-700"
      initial={{ scale: 0.9, opacity: 0, y: 20 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      exit={{ scale: 0.9, opacity: 0, y: 20 }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
    >
      {/* Header */}
      <div className="px-6 pt-5 rounded-t-lg">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Customize Your Report
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing || isLoading}
              className={`text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400 ${
                isRefreshing ? 'animate-spin' : ''
              }`}
              title="Refresh diagram and report data"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
            <button
              onClick={performClose} // Changed from handleClose
              className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
            <div className="px-6 py-4">
              <div className="flex gap-6">
                {/* Left Column - Settings */}
                <div className="w-1/2 space-y-4">
                  {/* Report Details Form */}
                  <ReportDetailsForm
                    formData={{
                      title,
                      authorName,
                      authorRole,
                      authorEmail,
                      selectedSections,
                      salesContact,
                      selectedSalesContactInfo,
                      diagramImage,
                      autoShare
                    }}
                    onChange={handleFormChange}
                    isAdmin={isAdmin}
                    salesContacts={salesContacts}
                    projectName={currentProject?.name}
                  />

                  {/* Advanced Section */}
                  <div className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-md dark:border dark:border-gray-700 dark:hover:border-gray-600 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-50">
                    <button
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      className="flex items-center justify-between w-full  text-sm font-medium text-left text-gray-700 dark:text-gray-300"
                    >
                      <span>Customize Content</span>
                      {showAdvanced ? (
                        <ChevronUp className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      )}
                    </button>
                    
                    <AnimatePresence>
                      {showAdvanced && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden my-2"
                        >
                          {/* Searchable Group List */}
                          <div>
                            <SearchableGroupList
                              searchQuery={searchQuery}
                              onSearchChange={setSearchQuery}
                              selectedSections={selectedSections}
                              onGroupToggle={handleGroupToggle}
                              onSectionToggle={handleSectionToggle}
                            />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Auto-share checkbox */}
                  <div className="mt-4">
                    <div className="flex items-center">
                      <div 
                        className="relative flex items-center justify-center mr-3 cursor-pointer"
                        onClick={() => !isUnsavedProject && setAutoShare(!autoShare)}
                      >
                        <input
                          type="checkbox"
                          checked={autoShare}
                          onChange={(e) => setAutoShare(e.target.checked)}
                          disabled={isUnsavedProject}
                          className="sr-only"
                          id="auto-share-checkbox"
                        />
                        <motion.div
                          initial={false}
                          animate={{
                            backgroundColor: autoShare && !isUnsavedProject
                              ? 'rgba(59, 130, 246, 1)'
                              : 'rgba(59, 130, 246, 0)',
                            borderColor: autoShare && !isUnsavedProject 
                              ? 'rgb(59, 130, 246)' 
                              : isUnsavedProject 
                                ? 'rgb(156, 163, 175)' 
                                : 'rgb(209, 213, 219)',
                            opacity: isUnsavedProject ? 0.5 : 1
                          }}
                          className="h-4 w-4 rounded border-2 flex items-center justify-center"
                        >
                          {autoShare && !isUnsavedProject && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="text-white"
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M8 11.5L11 15L16 9"
                                  stroke="white"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </motion.div>
                          )}
                        </motion.div>
                      </div>
                      <label 
                        htmlFor="auto-share-checkbox"
                        className={`text-sm ${isUnsavedProject ? 'text-gray-500 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'} cursor-pointer`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!isUnsavedProject) {
                            setAutoShare(!autoShare);
                          }
                        }}
                      >
                        Automatically share project and add link to report
                      </label>
                    </div>
                    {isUnsavedProject ? (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 pl-7 pr-10">
                        Save your project before generating the PDF to get access to an active link.
                      </p>
                    ) : (
                      <p className="text-xs text-gray-700 dark:text-gray-400 mt-2 pl-7 pr-10">
                        When checked, your project's access settings will be turned to public view.
                      </p>
                    )}
                  </div>
                </div>

                {/* Right Column - Actions */}
                <div className="w-1/2">
                  {/* Diagram Section */}
                  <DiagramSection
                    diagramImage={diagramImage}
                    onCrop={handleCropDiagram}
                    onRefresh={() => captureDiagramIfNeeded(true)}
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 rounded-b-lg border-t border-gray-200 dark:border-gray-800">
              <div className="flex justify-end gap-3">
                <button
                  onClick={performClose} // Changed from handleClose
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePreviewReport}
                  className={`px-4 py-2 text-sm font-medium text-white rounded ${isLoading ? 'bg-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                  disabled={isLoading}
                >
                  <span className="flex items-center">
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generating preview...
                      </>
                    ) : (
                      <>
                        <Download className="mr-2 h-4 w-4" />
                        Get Report
                      </>
                    )}
                  </span>
                </button>
              </div>
            </div>
            
            <AnimatePresence>
              {showCropDialog && (
                <CropDiagramDialog
                  key="crop-diagram-dialog"
                  open={showCropDialog} // This prop might be redundant if CropDiagramDialog is refactored
                  onClose={() => setShowCropDialog(false)}
                  onCropComplete={handleCropComplete}
                  initialImage={diagramImage}
                />
              )}
            </AnimatePresence>
    </motion.div>
  );
  
  return (
    <motion.div // This is the main overlay, always present if the dialog is to be shown
      className="fixed inset-0 bg-gray-950/50 dark:bg-gray-950/70 flex items-center justify-center z-[200]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }} // Consistent overlay transition
    >
      {showSaveDialog ? (
        <SaveProjectDialog
          open={true} // Or showSaveDialog, depending on SaveProjectDialog's API
          onClose={onClose} // If saving is cancelled, close the whole flow
          onSave={handleSaveProject}
          initialName={currentProject?.name}
          headerText="Save Project Before Customizing Report"
          // Assuming SaveProjectDialog is also a motion component or handles its own animation
        />
      ) : (
        mainDialogContent
      )}
    </motion.div>
  );
}

export default ReportCustomizationDialog;
